import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { DocumentIcon, DownloadIcon } from "@heroicons/react/outline";
import { downloadData, getUrl } from "aws-amplify/storage";

import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import ReportRevisionNotice from "../../../../Components/v2/Accounting/ReportRevisionNotice";
import Table from "../../../../Components/v2/Table";
import Pagination from "../../../../Components/v2/Pagination";
import Stats from "../../../../Components/v2/Stats";
import FormDateRange from "../../../../Components/v2/Form/FormDateRange";
import Modal from "../../../../Components/v2/Modal";

import { cancelAPIRequest, getAccountingFileName } from "../../../../Utils";
import {
  clearExportBalance,
  requestExportAccountBalance,
  requestListAccountBalance,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
} from "../../../../Context";
import { EXPORTS } from "../../../../Constants/accountingReport";
import {
  wibCurrentMonth,
  wibEndOfDay,
  wibStartOfDay,
} from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

// Stable, so the reset-page effect keyed on the rows doesn't fire every render.
const NO_ROWS = [];

const AccountingTrialBalance = () => {
  const { t } = useTranslation();
  const { user } = useAuthState();

  const [dateRange, setDateRange] = useState(wibCurrentMonth);
  const [startDate, endDate] = dateRange;
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Export & download state
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const downloadRef = useRef(null);
  const [downloadDocError, setDownloadDocError] = useState(null);
  const [showDownloadErrorModal, setShowDownloadErrorModal] = useState(false);
  const [showExportError, setShowExportError] = useState(false);

  const accountingDispatch = useAccountingDispatch();

  const {
    request,
    balanceAccounts: storedBalanceAccounts,
    balanceLoading,
    balanceError,
    balanceExportLoading,
    balanceExportError,
    balanceExportFile,
  } = useAccountingState();

  // With the range cleared nothing is requested, so the store still holds the
  // previous period. Show nothing rather than present it under an empty picker.
  const hasRange = Boolean(startDate && endDate);
  const balanceAccounts = hasRange ? storedBalanceAccounts : NO_ROWS;

  // --- Derived totals ---
  const totalDebit = (balanceAccounts ?? []).reduce(
    (sum, a) => sum + (a.debit || 0),
    0,
  );
  const totalCredit = (balanceAccounts ?? []).reduce(
    (sum, a) => sum + (a.credit || 0),
    0,
  );

  // Reset page when data changes (new filter applied)
  useEffect(() => {
    setPage(1);
  }, [balanceAccounts]);

  // Paged data (client-side slice)
  const allAccounts = balanceAccounts ?? [];
  const pagedData = allAccounts.slice((page - 1) * perPage, page * perPage);

  // --- Effects ---
  useEffect(() => {
    return () => {
      if (request) cancelAPIRequest(request);
      clearExportBalance(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user.mainBusiness?.id) return;
    if ((startDate && !endDate) || (!startDate && endDate)) return;
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.mainBusiness?.id, startDate, endDate]);

  useEffect(() => {
    if (balanceExportFile?.key) {
      downloadFile(balanceExportFile.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceExportFile]);

  useEffect(() => {
    if (balanceExportError) {
      setShowExportError(true);
    }
  }, [balanceExportError]);

  // --- Handlers ---
  const onLoad = () => {
    if (request) cancelAPIRequest(request);
    if (user.mainBusiness?.id && startDate && endDate) {
      requestListAccountBalance(accountingDispatch, {
        businessID: user.mainBusiness.id,
        startDate: wibStartOfDay(startDate),
        endDate: wibEndOfDay(endDate),
      });
    }
  };

  const handleExport = useCallback(
    (exportType) => {
      if (!startDate || !endDate) {
        alert(t("accounting.transactions.filter.dateRange"));
        return;
      }
      if (user.mainBusiness?.id) {
        requestExportAccountBalance(accountingDispatch, {
          businessID: user.mainBusiness.id,
          startDate: wibStartOfDay(startDate),
          endDate: wibEndOfDay(endDate),
          exportType,
        });
      }
    },
    [accountingDispatch, endDate, startDate, t, user.mainBusiness?.id],
  );

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    setDownloadProgress(totalBytes ? transferredBytes / totalBytes : 0);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setDownloading(true);
      const req = downloadData({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { onProgress: onDownloadProgress },
      });
      downloadRef.current = req;
      const result = await req.result;
      downloadBlob(result.body, getAccountingFileName(key));
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key);
      }
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
      clearExportBalance(accountingDispatch);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { useAccelerateEndpoint: true },
      });
      window.open(urlResult.url.href);
    } catch (signedURLError) {
      setDownloadDocError(signedURLError);
      setShowDownloadErrorModal(true);
    }
  };

  const exportActionItems = EXPORTS.map((item) => ({
    name: t(item.label),
    onClick: () => handleExport(item.value),
  }));

  // --- Table columns ---
  const columns = [
    {
      header: allAccounts.length > 1 ? t("Accounts") : t("Account"),
      accessor: (row) => {
        const { id, name, code } = row.account ?? {};
        const dateParams =
          startDate && endDate
            ? `&startDate=${wibStartOfDay(startDate)}&endDate=${wibEndOfDay(endDate)}`
            : "";
        if (code) {
          return (
            <a
              href={`/v2/accounting/transactions?accountId=${id}${dateParams}`}
              className="text-sm text-blue-600"
            >
              {`${code} - ${name}`}
            </a>
          );
        }
        return t(name);
      },
    },
    {
      header: t("Debit"),
      align: "right",
      accessor: (row) => (
        <NumericFormat
          displayType="text"
          value={row.debit}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix="Rp"
        />
      ),
    },
    {
      header: t("Credit"),
      align: "right",
      accessor: (row) => (
        <NumericFormat
          displayType="text"
          value={row.credit}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix="Rp"
        />
      ),
    },
  ];

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader
          title="Trial Balance"
          dropDownAction={{
            label: t("Export"),
            icon: <DownloadIcon className="h-5 w-5" />,
            onClick: () => handleExport("pdf"),
            items: exportActionItems,
            disabled: balanceExportLoading,
          }}
        />
        <ReportRevisionNotice
          businessID={user.mainBusiness?.id}
          startDate={startDate}
          endDate={endDate}
          className="border-b border-gray-200"
        />

        <div className="px-4 py-5 sm:px-6 lg:px-8">
          {/* Stats */}
          <div className="mb-6">
            <Stats
              isLoading={balanceLoading}
              stats={[
                // A dash, not Rp0, when there is no total to show: zero
                // reads as a real result on a financial report.
                {
                  title: t("Debit"),
                  value: balanceError || !hasRange ? "–" : totalDebit,
                },
                {
                  title: t("Credit"),
                  value: balanceError || !hasRange ? "–" : totalCredit,
                },
              ]}
            />
          </div>

          {/* Date filter */}
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormDateRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => setDateRange(update)}
              placeholderText={t("accounting.transactions.filter.dateRange")}
              onClear={() => setDateRange([null, null])}
              disabled={balanceLoading}
            />
          </div>

          {/* Table */}
          <Table
            isLoading={balanceLoading}
            error={balanceError}
            onRetry={onLoad}
            data={pagedData}
            columns={columns}
          />
        </div>

        <Pagination
          isOffline
          items={allAccounts}
          currentPage={page}
          isLoading={balanceLoading}
          perPage={perPage}
          onPageChange={(newPage) => setPage(newPage)}
          onPerPageChange={(pp) => {
            setPerPage(pp);
            setPage(1);
          }}
        />

        {/* Download progress modal */}
        <Modal
          title="Download File"
          open={downloading}
          onClose={() => {
            if (downloadRef.current) downloadRef.current.cancel();
            setDownloading(false);
          }}
          footer={
            <div className="w-full">
              <p className="text-xs text-slate-500 mb-1">{t("Downloading")}</p>
              <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.max(4, Math.round(downloadProgress * 100))}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          }
        >
          <div className="flex justify-center py-6">
            <DocumentIcon className="h-24 w-24 text-slate-400" />
          </div>
        </Modal>

        {/* Error modals */}
        {downloadDocError && (
          <ErrorDialog
            open={showDownloadErrorModal}
            error={downloadDocError}
            onClose={() => {
              setShowDownloadErrorModal(false);
              setDownloadDocError(null);
            }}
          />
        )}
        {balanceExportError && (
          <ErrorDialog
            open={showExportError}
            error={balanceExportError}
            onClose={() => {
              setShowExportError(false);
              clearExportBalance(accountingDispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingTrialBalance;
